/**
 * Sliding thumb for `.seg-pills`: the highlight follows the chosen button
 * instead of jumping. Positions `--thumb-x/w/h` on the group.
 */
const GROUP = '.seg-pills';
const lastActive = new WeakMap<HTMLElement, HTMLElement>();

function sync(el: HTMLElement): void {
  const active = el.querySelector<HTMLElement>(':scope > .active, :scope > [aria-checked="true"], :scope > [aria-selected="true"]');
  if (!active) {
    el.style.setProperty('--thumb-w', '0px');
    return;
  }
  // same button still growing/shrinking: track it without restarting the slide
  el.style.setProperty('--thumb-ease', lastActive.get(el) === active ? '0s' : '0.28s');
  lastActive.set(el, active);
  el.style.setProperty('--thumb-x', `${active.offsetLeft}px`);
  el.style.setProperty('--thumb-w', `${active.offsetWidth}px`);
  el.style.setProperty('--thumb-h', `${active.offsetHeight}px`);
}

export function watchSegPills(): void {
  const seen = new WeakSet<Element>();
  const ro = new ResizeObserver((entries) => {
    for (const e of entries) {
      const g = (e.target as HTMLElement).closest(GROUP);
      if (g instanceof HTMLElement) sync(g);
    }
  });
  const observe = (el: HTMLElement): void => {
    if (!seen.has(el)) {
      seen.add(el);
      ro.observe(el);
    }
    for (const child of el.children) {
      if (child instanceof HTMLElement && !seen.has(child)) {
        seen.add(child);
        ro.observe(child);
      }
    }
    sync(el);
  };
  let scheduled = false;
  const scan = (): void => {
    document.querySelectorAll<HTMLElement>(GROUP).forEach(observe);
  };
  const kick = (): void => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      scan();
    });
  };
  const mo = new MutationObserver((muts) => {
    for (const m of muts) {
      const t = m.target;
      if (t instanceof Element && (t.closest(GROUP) || t.querySelector?.(GROUP))) {
        kick();
        return;
      }
      if (m.addedNodes.length) {
        kick();
        return;
      }
    }
  });
  mo.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'aria-checked', 'aria-selected'] });
  scan();
}
